import { lazy, memo, Suspense } from "react"
import { cn } from "@/lib/utils"
import type { MarkdownProps } from "./markdown"

/**
 * The lazy boundary for the markdown chunk (decision 0029, proposal §6.2 and §10.1): Streamdown, the highlighter and
 * its grammars load only when a chat or transcript renders markdown, so the graph, home and settings pages never pay
 * for them. Until the chunk arrives, text renders as today's plain `pre-wrap` paragraph. App code imports
 * `Markdown`/`Fence` from here; tests import `./markdown` directly (a static render shows only the fallback).
 */

const LazyMarkdown = lazy(() => import("./markdown").then((m) => ({ default: m.Markdown })))
const LazyFence = lazy(() => import("./markdown").then((m) => ({ default: m.Fence })))

/** Start loading the chunk early (e.g. when a chat or transcript mounts), so the first message doesn't flash. */
export const preloadMarkdown = (): void => {
  void import("./markdown")
}

export const Markdown = memo(function Markdown(props: MarkdownProps) {
  return (
    <Suspense fallback={<p className={cn("whitespace-pre-wrap", props.className)}>{props.text}</p>}>
      <LazyMarkdown {...props} />
    </Suspense>
  )
})

export const Fence = memo(function Fence(props: {
  code: string
  lang: string
  className?: string | undefined
}) {
  return (
    <Suspense
      fallback={
        <pre className="overflow-x-auto rounded-control bg-ground px-2.5 py-2 font-mono text-[12px] whitespace-pre-wrap">
          {props.code}
        </pre>
      }
    >
      <LazyFence {...props} />
    </Suspense>
  )
})
